import { useState, useEffect } from "react";
import API from "../api";
import Select from "react-select";

function CreateOrderModal({ onClose, refreshOrders, refreshInventory }) {
  const today = new Date().toISOString().split("T")[0];

  const [customerId, setCustomerId] = useState("");
  const [orderDate, setOrderDate] = useState(today);
  const [remark, setRemark] = useState("");

  const [sheets, setSheets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [strips, setStrips] = useState([]);

  const [sheetId, setSheetId] = useState("");
  const [quantity, setQuantity] = useState("1"); // ✅ default 1
  const [selectedStrips, setSelectedStrips] = useState([]);
  const [isReplacement, setIsReplacement] = useState(false);

  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    API.get("inventory/get-inventory/").then(res => setSheets(res.data));
    API.get("inventory/strip-list/").then(res => setStrips(res.data));
    API.get("order/get-customers/").then(res => setCustomers(res.data));
  }, []);

  const addItem = () => {
    if (!sheetId || !quantity || selectedStrips.length === 0) return;

    const sheet = sheets.find(s => s.id == sheetId);

    const existingIndex = items.findIndex(
      item => item.sheet_id == sheetId
    );

    if (existingIndex !== -1) {
      const updatedItems = [...items];

      updatedItems[existingIndex].quantity_sheets =
        parseInt(updatedItems[existingIndex].quantity_sheets) +
        parseInt(quantity);

      const mergedStrips = [
        ...new Set([
          ...updatedItems[existingIndex].strip_ids,
          ...selectedStrips
        ])
      ];

      updatedItems[existingIndex].strip_ids = mergedStrips;

      updatedItems[existingIndex].strips_display = strips
        .filter(s => mergedStrips.includes(s.id))
        .map(s => s.size_mm);

      updatedItems[existingIndex].is_replacement =
        updatedItems[existingIndex].is_replacement || isReplacement;

      setItems(updatedItems);

    } else {
      setItems([
        ...items,
        {
          sheet_id: sheetId,
          sheet_name: `${sheet.name}`,
          quantity_sheets: quantity,
          strip_ids: selectedStrips,
          is_replacement: isReplacement,
          strips_display: strips
            .filter(s => selectedStrips.includes(s.id))
            .map(s => s.size_mm)
        }
      ]);
    }

    // reset line
    setSheetId("");
    setQuantity("1");
    setSelectedStrips([]);
    setIsReplacement(false);
  };

  const saveOrder = async () => {
    let tempItems = [...items];

    // ✅ Auto-add single item if not added
    if (
      tempItems.length === 0 &&
      sheetId &&
      quantity &&
      selectedStrips.length > 0
    ) {
      tempItems.push({
        sheet_id: sheetId,
        quantity_sheets: quantity,
        strip_ids: selectedStrips,
        is_replacement: isReplacement
      });
    }

    let newErrors = {};

    if (!customerId) newErrors.customer = "Customer is required";
    if (!orderDate) newErrors.date = "Order date is required";
    if (tempItems.length === 0)
      newErrors.items = "Add at least one item";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    await API.post("order/create-order/", {
      customer_id: customerId,
      order_date: orderDate,
      remark: remark,
      items: tempItems.map(i => ({
        sheet_id: i.sheet_id,
        quantity_sheets: i.quantity_sheets,
        strip_ids: i.strip_ids,
        is_replacement: i.is_replacement
      }))
    });

    refreshOrders();
    refreshInventory();
    onClose();
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "32px",
      fontSize: "0.875rem"
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px"
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "32px"
    })
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">

          <div className="modal-header">
            <h6 className="modal-title">New Order</h6>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">

            {/* Customer + Date + Remark */}
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <Select
                  options={customers.map(c => ({
                    value: c.id,
                    label: c.name
                  }))}
                  value={
                    customers
                      .filter(c => c.id == customerId)
                      .map(c => ({ value: c.id, label: c.name }))[0] || null
                  }
                  onChange={(selected) =>
                    setCustomerId(selected ? selected.value : "")
                  }
                  placeholder="Select Customer..."
                  isClearable
                  styles={customSelectStyles}
                />
                {errors.customer && (
                  <small className="text-danger">{errors.customer}</small>
                )}
              </div>

              <div className="col-md-3">
                <input
                  type="date"
                  className={`form-control form-control-sm ${errors.date ? "is-invalid" : ""}`}
                  value={orderDate}
                  onChange={e => setOrderDate(e.target.value)}
                />
                {errors.date && (
                  <div className="invalid-feedback d-block">
                    {errors.date}
                  </div>
                )}
              </div>

              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Remark (optional)"
                  value={remark}
                  onChange={e => setRemark(e.target.value)}
                />
              </div>
            </div>

            <hr />

            {/* Line Item Section */}
            <div className="row g-2 align-items-center mb-2">
              <div className="col-md-4">
                <Select
                  options={sheets.map(sheet => ({
                    value: sheet.id,
                    label: `${sheet.name}`
                  }))}
                  value={
                    sheets
                      .filter(s => s.id == sheetId)
                      .map(s => ({
                        value: s.id,
                        label: `${s.name}`
                      }))[0] || null
                  }
                  onChange={(selected) =>
                    setSheetId(selected ? selected.value : "")
                  }
                  placeholder="Select Sheet..."
                  isClearable
                  styles={customSelectStyles}
                />
              </div>

              <div className="col-md-2">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={quantity}
                  min="1"
                  onChange={e => setQuantity(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <Select
                  isMulti
                  options={strips.map(strip => ({
                    value: strip.id,
                    label: `${strip.size_mm}`
                  }))}
                  value={strips
                    .filter(s => selectedStrips.includes(s.id))
                    .map(s => ({
                      value: s.id,
                      label: `${s.size_mm}`
                    }))
                  }
                  onChange={(selected) =>
                    setSelectedStrips(
                      selected ? selected.map(s => s.value) : []
                    )
                  }
                  placeholder="Select Strips..."
                />
              </div>

              <div className="col-md-2 d-flex align-items-center">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isReplacement}
                    onChange={(e) =>
                      setIsReplacement(e.target.checked)
                    }
                  />
                  <label className="form-check-label small">
                    Replacement
                  </label>
                </div>
              </div>

              <div className="col-md-1">
                <button
                  className="btn btn-sm btn-success w-100"
                  onClick={addItem}
                >
                  +
                </button>
              </div>
            </div>

            {errors.items && (
              <div className="text-danger mb-2">
                {errors.items}
              </div>
            )}

            {/* Items Table */}
            {items.length > 0 && (
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Sheet</th>
                    <th>Qty</th>
                    <th>Strips</th>
                    <th>Replacement</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.sheet_name}</td>
                      <td>{item.quantity_sheets}</td>
                      <td>{item.strips_display.join(", ")}</td>
                      <td>
                        {item.is_replacement ? "Yes" : "No"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          </div>

          <div className="modal-footer">
            <button
              className="btn btn-sm btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={saveOrder}
            >
              Save
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CreateOrderModal;